import { Link } from "react-router-dom";
import { useSeo } from "@/utils/seo";
import { ArrowUpRight, FileDown } from "lucide-react";
import { MANUALS } from "@/data/downloads";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

export default function ManualsPage() {
    const { t } = useLang();
    useSeo(
        t("Bedienungsanleitungen als PDF | Müller Satellitentechnik", "Operating manuals as PDF | Müller satellite technology"),
        t(
            "Bedienungsanleitungen für Kopfstationen, Umsetzer und Antennenheizungen — als PDF direkt von Müller Satelliten- und Antennentechnik.",
            "Operating manuals for headends, transmodulators and antenna heating — as PDFs straight from Müller Satelliten- und Antennentechnik."
        ),
    );
    return (
        <main data-testid="page-manuals" className="pt-28 sm:pt-32">
            <section className="py-10 sm:py-14">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid="breadcrumb">
                        <Link to="/downloads" data-testid="breadcrumb-downloads" className="hover:text-red-700 transition-colors">
                            {t("Download", "Downloads")}
                        </Link>
                        <span className="mx-2">/</span>
                        <span className="text-slate-600">{t("Bedienungsanleitungen", "Operating manuals")}</span>
                    </p>
                    <Reveal>
                        <Eyebrow>{t("Bedienungsanleitungen", "Operating manuals")}</Eyebrow>
                        <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                            {t("Alle Anleitungen als PDF.", "All manuals as PDFs.")}
                        </h1>
                        <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl text-sm sm:text-base">
                            {t(
                                "Bedienungsanleitungen für Kopfstationstechnik, Einzelumsetzer, Einkabelsysteme, Antennenheizungen und den Infomediaplayer — zum direkten Öffnen und Speichern.",
                                "Operating manuals for headend technology, transmodulators, single-cable systems, antenna heating and the InfoMedia player — open and save them directly."
                            )}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-16 sm:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 space-y-10">
                    {MANUALS.map((group, gi) => (
                        <div key={group.category}>
                            <Reveal>
                                <h2 className="font-mono text-xs uppercase tracking-[0.18em] text-red-700 border-b border-slate-200 pb-3" data-testid={`manuals-group-${gi}`}>
                                    {group.category}
                                </h2>
                            </Reveal>
                            <div className="divide-y divide-slate-100 rounded-b-xl">
                                {group.items.map((m, i) => (
                                    <Reveal key={m.name} delay={Math.min(i * 0.04, 0.2)}>
                                        <a
                                            href={m.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`manual-${gi}-${i}`}
                                            className="group flex items-center justify-between gap-4 py-4 px-2 -mx-2 rounded-lg hover:bg-white hover:shadow-sm transition-all duration-200"
                                        >
                                            <span className="font-semibold text-sm sm:text-base text-slate-900">{m.name}</span>
                                            <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-red-700 border-b border-red-300 pb-0.5">
                                                {t("PDF öffnen", "Open PDF")}
                                                <FileDown className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
                                            </span>
                                        </a>
                                    </Reveal>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div>
                        <Link
                            to="/downloads"
                            data-testid="back-to-downloads"
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-red-700 transition-colors"
                        >
                            <ArrowUpRight className="h-4 w-4 rotate-180" />
                            {t("Zurück zu allen Downloads", "Back to all downloads")}
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
